import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { photoUrl, friendlyError } from "@/lib/api";
import { ageRangeLabelT } from "@/lib/labels";
import { casesApi, claimsApi } from "@/lib/endpoints";
import { validateClaim } from "@/lib/validation";
import { ShareButton } from "@/components/ShareButton";
import { mapsAvailable, ZoneMap } from "@/components/GoogleMaps";
import { ReportAbuseButton, WithdrawSection } from "@/components/ModerationActions";
import { HandoffTimeline } from "@/components/HandoffTimeline";
import { PhoneVerify } from "@/components/PhoneVerify";

export default function CaseDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [idToken, setIdToken] = useState("");
  const [form, setForm] = useState({ claimant_name: "", cedula: "", relationship: "", claimant_phone: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    casesApi.get(id).then(setC).catch(() => toast.error(t("caseDetail.notFound")))
      .finally(() => setLoading(false));
  }, [id, t]);

  const submit = async (e) => {
    e.preventDefault();
    const error = validateClaim(form, c.name_unknown, t);
    if (error) { toast.error(error); return; }
    if (!idToken) { toast.error(t("caseDetail.toastVerify")); return; }
    setSubmitting(true);
    try {
      await claimsApi.create(id, { ...form, firebase_id_token: idToken });
      setSent(true);
      toast.success(t("caseDetail.toastClaimSent"));
    } catch (err) {
      toast.error(friendlyError(err, t("caseDetail.toastClaimError")));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p className="text-center text-neutral-500 py-10">{t("common.loading")}</p>;
  if (!c) return <p className="text-center text-neutral-500 py-10">{t("caseDetail.notFoundDot")}</p>;

  const labelCls = "text-sm font-medium text-neutral-900 mb-1.5 block";
  const inputCls = "w-full min-h-[48px] rounded-sm border border-neutral-300 bg-white px-3 text-base focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC] outline-none";

  return (
    <div className="flex flex-col gap-4" data-testid="case-detail">
      <Link to="/" className="text-sm text-neutral-500 hover:text-[#0066CC] flex items-center gap-1" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> {t("common.backToSearch")}
      </Link>

      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
        <div className="aspect-[4/3] bg-neutral-200">
          <img src={photoUrl(c.photo_path)} alt={c.first_name} className="w-full h-full object-cover" />
        </div>
        <div className="p-4 flex flex-col gap-2">
          <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="case-name">
            {c.name_unknown ? t("card.nameUnknown") : c.first_name}
          </h1>
          <p className="text-neutral-700">{ageRangeLabelT(c.age_range, t)}</p>
          {c.name_unknown && (
            <div className="text-sm text-neutral-600 flex flex-col gap-0.5">
              {c.eye_color && <span>{t("caseDetail.eyes")} {c.eye_color}</span>}
              {c.skin_color && <span>{t("caseDetail.skin")} {c.skin_color}</span>}
              {c.distinguishing_marks && <span>{t("caseDetail.marks")} {c.distinguishing_marks}</span>}
              {c.clothing && <span>{t("caseDetail.clothing")} {c.clothing}</span>}
            </div>
          )}
          <p className="text-sm text-neutral-500 uppercase tracking-wide flex items-center gap-1">
            <MapPin className="w-4 h-4" /> {c.municipality}{c.state ? `, ${c.state}` : ""}
          </p>
          {c.status === "localizada" ? (
            <span className="text-sm font-semibold text-[#166534]">{t("card.familyLocated")}</span>
          ) : (
            <span className="text-sm font-semibold text-[#B45309]">{t("card.searchingFamily")}</span>
          )}
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-sm p-4 flex flex-col gap-2">
        <p className="text-sm font-semibold text-neutral-900">{t("caseDetail.shareTitle")}</p>
        <p className="text-xs text-neutral-500">{t("caseDetail.shareHint")}</p>
        <ShareButton url={`/caso/${c.id}`} testid={`share-case-${c.id}`}
          text={t("card.shareCaseText", { name: c.name_unknown ? t("card.thisMinor") : c.first_name, muni: c.municipality })} />
      </div>

      {mapsAvailable() && (
        <div className="bg-white border border-neutral-200 rounded-sm p-4 flex flex-col gap-2">
          <p className="text-sm font-semibold text-neutral-900">{t("caseDetail.zoneTitle")}</p>
          <p className="text-xs text-neutral-500">{t("caseDetail.zoneHint")}</p>
          <ZoneMap municipality={`${c.municipality}, ${c.state || ""}`} />
        </div>
      )}

      <HandoffTimeline caseId={c.id} />

      {c.status === "localizada" ? (
        <div className="text-center text-sm text-neutral-600 bg-white border border-neutral-200 rounded-sm p-4">
          {t("caseDetail.alreadyReunited")}
        </div>
      ) : sent ? (
        <div className="bg-white border border-neutral-200 rounded-sm p-4 text-center flex flex-col items-center gap-2" data-testid="claim-success">
          <ShieldCheck className="w-10 h-10 text-[#166534]" />
          <p className="font-semibold">{t("caseDetail.claimSentTitle")}</p>
          <p className="text-sm text-neutral-600">
            {t("caseDetail.claimSentText")}
          </p>
        </div>
      ) : !showForm ? (
        <button onClick={() => setShowForm(true)} data-testid="open-claim-form-btn"
          className="bg-[#0066CC] text-white hover:bg-[#0052A3] rounded-sm min-h-[48px] font-semibold">
          {t("caseDetail.openClaim")}
        </button>
      ) : (
        <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-sm p-4 flex flex-col gap-3" data-testid="claim-form">
          <p className="text-sm text-neutral-600">
            {t("caseDetail.claimFormHint")}
          </p>
          <div>
            <label className={labelCls}>{t("caseDetail.yourFullName")}</label>
            <input data-testid="claim-name" className={inputCls} value={form.claimant_name} onChange={set("claimant_name")} />
          </div>
          <div>
            <label className={labelCls}>{t("caseDetail.cedula")}</label>
            <input data-testid="claim-cedula" className={inputCls} value={form.cedula} onChange={set("cedula")} inputMode="numeric" />
          </div>
          <div>
            <label className={labelCls}>{t("caseDetail.relationship")}</label>
            <input data-testid="claim-relationship" className={inputCls} value={form.relationship} onChange={set("relationship")}
              placeholder={t("caseDetail.relationshipPlaceholder")} />
          </div>
          <div>
            <label className={labelCls}>{t("caseDetail.claimPhone")}</label>
            <PhoneVerify testid="claim-verify" phone={form.claimant_phone}
              onPhoneChange={(v) => setForm((f) => ({ ...f, claimant_phone: v }))}
              verified={!!idToken} onVerified={setIdToken} onReset={() => setIdToken("")} />
          </div>
          {c.name_unknown && (
            <div className="flex flex-col gap-3 border-t border-neutral-100 pt-3" data-testid="claim-evidence-block">
              <p className="text-sm font-medium text-neutral-900">{t("caseDetail.evidenceTitle")}</p>
              <div>
                <label className={labelCls}>{t("caseDetail.clothingWorn")}</label>
                <input data-testid="claim-clothing" className={inputCls} value={form.clothing_description} onChange={set("clothing_description")} />
              </div>
              <div>
                <label className={labelCls}>{t("caseDetail.marksLabel")}</label>
                <input data-testid="claim-marks" className={inputCls} value={form.marks_description} onChange={set("marks_description")} />
              </div>
              <div>
                <label className={labelCls}>{t("caseDetail.lostDate")}</label>
                <input data-testid="claim-lost-date" type="date" className={inputCls} value={form.lost_contact_date} onChange={set("lost_contact_date")} />
              </div>
              <div>
                <label className={labelCls}>{t("caseDetail.lostPlace")}</label>
                <input data-testid="claim-lost-place" className={inputCls} value={form.lost_contact_place} onChange={set("lost_contact_place")} />
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowForm(false)} data-testid="cancel-claim-btn"
              className="flex-1 bg-white border border-neutral-300 hover:bg-neutral-50 rounded-sm min-h-[48px] font-semibold">
              {t("common.cancel")}
            </button>
            <button type="submit" disabled={submitting || !idToken} data-testid="submit-claim-btn"
              className="flex-1 bg-[#0066CC] text-white hover:bg-[#0052A3] disabled:opacity-60 rounded-sm min-h-[48px] font-semibold flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : t("caseDetail.submitClaim")}
            </button>
          </div>
        </form>
      )}

      <div className="flex items-center justify-between gap-2">
        <ReportAbuseButton testid={`report-case-${c.id}`} onReport={(reason) => casesApi.reportAbuse(c.id, reason)} />
      </div>

      <WithdrawSection testid={`withdraw-case-${c.id}`} label={t("caseDetail.withdrawLabel")}
        onWithdraw={(phone) => casesApi.withdraw(c.id, phone)} />
    </div>
  );
}
